import { Modal, Button, Select } from '@cimpress/react-components';
import { useState, useEffect } from 'react';
import type { TimezoneDetails } from '@comet-ui-components/comet-header-component';
import { FulfillerItem, SelectedFulfillerConfig } from '@/models/HeaderTypes';
import { AVAILABLE_TIMEZONES, DEFAULT_TIMEZONE } from '@/constants';
import { useNavigate } from 'react-router-dom';

export interface LabelValuePair {
  label: string;
  value: string;
}

export interface TenantSelection {
  fulfiller: LabelValuePair;
  fulfillerLocation: LabelValuePair;
  xCometAccountId: string;
}

interface TenantSelectorProps {
  fulfillerList: FulfillerItem[];
  handleFulfillerSelection: (
    selectedFulfillerConfig: SelectedFulfillerConfig | null,
  ) => void;
  handleTimezoneSelection?: (timezone: TimezoneDetails) => void;
  selectedFulfillerConfig: TenantSelection | undefined;
  selectedTimezone?: TimezoneDetails;
  selectedCometAccountId?: string;
  showModal?: boolean;
  onModalClose?: () => void;
  onModalOpen?: () => void;
}

const TenantSelector = ({
  fulfillerList,
  // handleSelection,
  handleFulfillerSelection,
  handleTimezoneSelection,
  selectedFulfillerConfig,
  selectedTimezone,
  selectedCometAccountId,
  showModal = false,
  onModalClose,
  onModalOpen,
}: TenantSelectorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(showModal);
  const [activeTenantConfig, setActiveTenantConfig] = useState<
    TenantSelection | undefined
  >(selectedFulfillerConfig);

  const [activeTimezone, setActiveTimezone] = useState<
    TimezoneDetails | undefined
  >(selectedTimezone || DEFAULT_TIMEZONE);

  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeCometAccountId, setActiveCometAccountId] = useState<
    string | undefined
  >(selectedCometAccountId || '');

  const [tenantDisplayName, setTenantDisplayName] = useState<string>(
    activeTenantConfig?.fulfiller.label || '--Select Tenant--',
  );

  // Sync modal state with showModal prop
  useEffect(() => {
    setIsModalOpen(showModal);
  }, [showModal]);

  useEffect(() => {
    setActiveTenantConfig(selectedFulfillerConfig);
    setTenantDisplayName(
      selectedFulfillerConfig?.fulfiller.label || '--Select Tenant--',
    );
  }, [selectedFulfillerConfig]);

  useEffect(() => {
    setActiveTimezone(selectedTimezone || DEFAULT_TIMEZONE);
  }, [selectedTimezone]);

  useEffect(() => {
    setActiveCometAccountId(selectedCometAccountId || '');
  }, [selectedCometAccountId]);

  const handleOpenModal = () => {
    // Initialize first tenant if none is selected
    if (!activeTenantConfig && fulfillerList.length > 0) {
      const defaultFulfiller = fulfillerList[0];
      setActiveTenantConfig({
        fulfiller: defaultFulfiller.fulfiller,
        fulfillerLocation: defaultFulfiller.fulfillerLocations[0],
        xCometAccountId: defaultFulfiller.xCometAccountId,
      });
    }
    setIsModalOpen(true);
    if (onModalOpen) onModalOpen();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (onModalClose) onModalClose();
  };

  const handleTenantSelect = (selectedOption: LabelValuePair | null) => {
    if (selectedOption && typeof selectedOption.value === 'string') {
      const selectedFulfiller = fulfillerList.find(
        (t) => t.fulfiller.value === selectedOption.value,
      );

      if (selectedFulfiller) {
        setActiveTenantConfig({
          fulfiller: selectedFulfiller.fulfiller,
          fulfillerLocation: selectedFulfiller.fulfillerLocations[0],
          xCometAccountId: selectedFulfiller.xCometAccountId,
        });

        setActiveCometAccountId(selectedFulfiller.xCometAccountId);
      }
    }
  };

  const handleLocationSelect = (selectedOption: LabelValuePair | null) => {
    if (selectedOption && activeTenantConfig) {
      const selectedFulfiller = fulfillerList.find(
        (t) => t.fulfiller.value === activeTenantConfig.fulfiller.value,
      );
      if (selectedFulfiller) {
        setActiveTenantConfig({
          fulfiller: activeTenantConfig.fulfiller,
          fulfillerLocation:
            selectedFulfiller.fulfillerLocations.find(
              (loc) => loc.value === selectedOption.value,
            ) || selectedFulfiller.fulfillerLocations[0],
          xCometAccountId: selectedFulfiller.xCometAccountId,
        });

        setActiveCometAccountId(selectedFulfiller.xCometAccountId);
      }
    }
  };

  const handleTimezoneSelect = (selectedOption: LabelValuePair | null) => {
    if (selectedOption && typeof selectedOption.value === 'string') {
      const selectedTz = AVAILABLE_TIMEZONES.find(
        (tz) => tz.value === selectedOption.value,
      );
      if (selectedTz) setActiveTimezone(selectedTz);
    }
  };

  const handleApplySelection = () => {
    handleFulfillerSelection(activeTenantConfig || null);
    if (handleTimezoneSelection && activeTimezone) {
      handleTimezoneSelection(activeTimezone);
    }

    setTenantDisplayName(
      activeTenantConfig?.fulfiller.label || '--Select Tenant--',
    );
    if (activeTenantConfig?.fulfillerLocation) {
      navigate(
        `/entitytypes?tenantId=${activeTenantConfig?.fulfillerLocation.value}`,
        { replace: true },
      );
    }
    handleCloseModal();
  };

  return (
    <>
      <Button
        style={{ padding: '0 12px' }}
        variant="secondary"
        onClick={handleOpenModal}
      >
        {tenantDisplayName}
      </Button>

      <Modal
        show={isModalOpen}
        onRequestHide={handleCloseModal}
        title={'Select Fulfiller'}
        size="md"
      >
        <Select
          key={`select-${activeTenantConfig?.fulfiller.value || 'none'}`}
          className="select-fulfiller"
          label="Fulfiller"
          value={activeTenantConfig?.fulfiller}
          options={fulfillerList.map((f) => ({
            label: f.fulfiller.label,
            value: f.fulfiller.value,
          }))}
          onChange={handleTenantSelect}
        />

        <Select
          className="select-location"
          label="Location"
          value={activeTenantConfig?.fulfillerLocation}
          options={
            activeTenantConfig
              ? fulfillerList
                  .find(
                    (f) =>
                      f.fulfiller.value === activeTenantConfig.fulfiller.value,
                  )
                  ?.fulfillerLocations.map((loc) => ({
                    label: loc.label,
                    value: loc.value,
                  })) || []
              : []
          }
          onChange={handleLocationSelect}
        />

        {AVAILABLE_TIMEZONES.length > 0 && (
          <Select
            className="select-timezone"
            label="Timezone"
            value={activeTimezone}
            options={AVAILABLE_TIMEZONES.map((tz) => ({
              label: tz.label,
              value: tz.value,
            }))}
            onChange={handleTimezoneSelect}
          />
        )}

        <div
          style={{
            marginTop: '20px',
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
          }}
        >
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleApplySelection}>
            Apply
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default TenantSelector;
